<script setup>
import { ref, watch } from "vue";
import { Head, useForm } from "@inertiajs/vue3";
import Select from "./Components/Form/Select.vue";
import SaveButton from "./Components/Form/SaveButton.vue";
import Button from "./Components/Button.vue";
import Faq from "./Components/Faq.vue";
import { router } from "@inertiajs/vue3";
import CustomLink from "./Components/CustomLink.vue";
import Subfooter from "./Includes/Subfooter.vue";

const eurFormatter = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
});

const props = defineProps({
    vehicles: Array,
});

const minYear = Math.min(...props.vehicles.map(vehicle => vehicle.year));
const maxYear = Math.max(...props.vehicles.map(vehicle => vehicle.year));
const constructionYears = ref(
    Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(year => ({ id: year, label: year }))
);

const mileages = ref(
    Array.from({ length: 400000 / 10000 }, (_, i) => i * 10000).map(mileage => ({ id: mileage, label: mileage.toLocaleString() + ' km' }))
);

const prices = ref(
    Array.from({ length: 150000 / 10000 }, (_, i) => i * 10000).map(price => ({ id: price, label: eurFormatter.format(price) }))
);

const searchForm = useForm({
    brand: null,
    model: null,
    type: null,
    price: [null, null],
    construction_year: [null, null],
    mileage: [null, null],
    fuel: null,
    transmission: null,
    body: null
})

//watch all form values
const brandOptions = ref([...new Set(props.vehicles.map(vehicle => vehicle.brand))].map(brand => ({ id: brand, label: brand })));

const modelOptions = ref([]);
watch(
    () => searchForm.brand,
    (newBrand) => {
        if (newBrand) {
            modelOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === newBrand)
                    .map(vehicle => vehicle.model)
            )].map(model => ({ id: model, label: model }));
        } else {
            modelOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when brand has changed, model should be reset
        searchForm.model = null;
        searchForm.type = null;
    }
);

const typeOptions = ref([]);
watch(
    () => searchForm.model,
    (newModel) => {
        if (newModel) {
            typeOptions.value = [...new Set(
                props.vehicles
                    .filter(vehicle => vehicle.brand === searchForm.brand)
                    .filter(vehicle => vehicle.model === newModel)
                    .map(vehicle => vehicle.type)
            )].map(type => ({ id: type, label: type }));
        } else {
            typeOptions.value = [{ id: null, label: 'Geen resultaten gevonden' }];
        }

        //when model has changed, type should be reset
        searchForm.type = null;
    }
);


const bodyTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.body))].map(body => ({ id: body, label: body })));
const fuelTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.fuel))].map(fuel => ({ id: fuel, label: fuel })));
const transmissionTypes = ref([...new Set(props.vehicles.map(vehicle => vehicle.transmission))].map(transmission => ({ id: transmission, label: transmission })));

function submit() {
    const filterParams = {
        brand: searchForm.brand,
        model: searchForm.model,
        type: searchForm.type,
        body: searchForm.body,
        fuel: searchForm.fuel,
        transmission: searchForm.transmission,
        price: searchForm.price,
        construction_year: searchForm.construction_year,
        mileage: searchForm.mileage
    };

    setParams(filterParams);
}

function popularYear() {
    const filterParams = {
        construction_year: [2014, maxYear],
    };

    setParams(filterParams);
}

function popularMileage() {
    const filterParams = {
        mileage: [0, 150000],
    };

    setParams(filterParams);
}

function popularTransmission() {
    const filterParams = {
        transmission: 'Automaat',
    };

    setParams(filterParams);
}

function popularBody() {
    const filterParams = {
        body: 'SUV',
    };

    setParams(filterParams);
}

function popularPrice() {
    const filterParams = {
        price: [0, 25000],
    };

    setParams(filterParams);
}

function popularFuel() {
    const filterParams = {
        fuel: 'Benzine-Elektrisch',
    };

    setParams(filterParams);
}

function setParams(params) {
    localStorage.setItem('filterParams', JSON.stringify(params));

    router.visit('/aanbod');
}
</script>
<template>

    <Head>
        <title>Zoeken naar Gebruikte Auto's bij Zwagers Automotive</title>
    </Head>
    <section class="relative flex flex-col bg-black lg:-space-y-56">
        <!--hero-->
        <div class="z-10 text-white relative lg:h-screen lg:min-h-[768px] max-h-[1200px] flex flex-col">
            <div class="relative flex justify-center">
                <img :src="'/img/showroom/zwagers_automotive_show_25.webp'" alt="Zwagers Automotive" class="absolute block lg:hidden object-cover w-full h-full opacity-40">
                <div class="relative z-10 w-11/12 px:0 lg:px-6 xl:px-0 lg:h-5/6 max-w-6xl lg:min-h-[768px] py-24 lg:py-0">
                    <div class="flex flex-col gap-10 lg:h-screen justify-center text-white">
                        <h1>Vind jouw auto</h1>
                        <form @submit.prevent="submit">
                            <div class="mt-4 grid grid-cols-1 sm:grid-cols-3 text-sm font-normal text-gray-900 gap-3 md:gap-5 z-40">
                                <div class="col-span-full sm:col-span-1">
                                    <Select v-model="searchForm.brand" :options="brandOptions" label="Merk" valueName="label" clear valueId="id" placeholder="Selecteer een merk..." labelClass="text-white" />
                                </div>
                                <div class="col-span-full sm:col-span-1">
                                    <Select v-model="searchForm.model" :options="modelOptions" label="Model" valueName="label" clear valueId="id" placeholder="Selecteer een model..." labelClass="text-white" />
                                </div>
                                <div class="col-span-full sm:col-span-1">
                                    <Select v-model="searchForm.type" :options="typeOptions" label="Variant" valueName="label" clear valueId="id" placeholder="Selecteer een type..." labelClass="text-white" />
                                </div>
                                <div class="col-span-full sm:col-span-1">
                                    <Select v-model="searchForm.body" :options="bodyTypes" label="Carrosserie" valueName="label" clear valueId="id" placeholder="Selecteer een carrosserie..." labelClass="text-white" />
                                </div>
                                <div class="col-span-full sm:col-span-1">
                                    <Select v-model="searchForm.fuel" :options="fuelTypes" label="Brandstof" valueName="label" clear valueId="id" placeholder="Selecteer een brandstof..." labelClass="text-white" />
                                </div>
                                <div class="col-span-full sm:col-span-1">
                                    <Select v-model="searchForm.transmission" :options="transmissionTypes" label="Transmissie" valueName="label" clear valueId="id" placeholder="Selecteer een transmissie..." labelClass="text-white" />
                                </div>
                                <div class="col-span-full sm:col-span-1 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-2 lg:gap-4">
                                    <div>
                                        <Select :options="prices" valueName="label" valueId="id" label="Prijs" v-model="searchForm.price[0]" placeholder="Van" labelClass="text-white" />
                                    </div>
                                    <div class="content-end">
                                        <Select :options="prices" valueName="label" valueId="id" label="" v-model="searchForm.price[1]" placeholder="Tot"labelClass="text-white" />
                                    </div>
                                </div>
                                <div class="col-span-full sm:col-span-1 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-2 lg:gap-4">
                                    <div>
                                        <Select v-model="searchForm.construction_year[0]" :options="constructionYears" label="Bouwjaar" valueName="label" valueId="id" placeholder="Van" labelClass="text-white" />
                                    </div>
                                    <div class="content-end">
                                        <Select v-model="searchForm.construction_year[1]" :options="constructionYears" label="" valueName="label" valueId="id" placeholder="Tot" labelClass="text-white" />
                                    </div>
                                </div>
                                <div class="col-span-full sm:col-span-1 grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-2 lg:gap-4">
                                    <div>
                                        <Select v-model="searchForm.mileage[0]" :options="mileages" label="Kilometerstand" valueName="label" valueId="id" placeholder="Van" labelClass="text-white" />
                                    </div>
                                    <div class="content-end">
                                        <Select v-model="searchForm.mileage[1]" :options="mileages" label="" valueName="label" valueId="id" placeholder="Tot" labelClass="text-white" />
                                    </div>
                                </div>
                                <div>
                                    <CustomLink href="/uitgebreidzoeken" class="text-white button-animation hover:text-tan">Meer filters</CustomLink>
                                </div>
                                <div class="hidden sm:block"></div>
                                <SaveButton href="/aanbod" class="col-span-full sm:col-span-1 primary-button h-full mt-auto" text="Resultaten tonen" />
                            </div>
                        </form>
                    </div>
                </div>
                <div class="absolute h-24 lg:hidden bg-gradient-to-b from-black/5 from-35% to-black bottom-0 left-0 right-0 w-full" />
            </div>
        </div>
        <div class="flex justify-center lg:z-20 bg-gradient-to-b from-black/5 to-35% to-black text-white pb-20">
            <div class="w-11/12 px:0 md:px-6 xl:px-0 flex flex-col gap-10 max-w-6xl">
                <div class="flex flex-col gap-4 pb-6 pt-20">
                    <h2 class="max-w-6xl text-center">Populaire zoektermen</h2>
                </div>
                <div class="text-white text-left text-nowrap mx-auto w-full items-center max-w-6xl flex flex-col sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 md:gap-10">
                    <Button class="group text-white hover:text-tanhover flex flex-col w-48" :useInertia="false" @click="popularYear">
                        <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                            <font-awesome-icon class="h-8" :icon="['fas','calendar-week']" />
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">vanaf 2014</span>
                        </div>
                        <span class="text-base/7 text-gray-400">Bouwjaar</span>
                    </Button>
                    <Button class="group text-white hover:text-tanhover flex flex-col w-48 md:mx-auto" :useInertia="false" @click="popularMileage">
                        <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                            <font-awesome-icon class="h-8" :icon="['fas','gauge-low']" />
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">tot 150.000</span>
                        </div>
                        <span class="text-base/7 text-gray-400">Kilometerstand</span>
                    </Button>
                    <Button class="group text-white hover:text-tanhover flex flex-col w-48 md:ml-auto" :useInertia="false" @click="popularTransmission">
                        <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                            <svg width="32" height="32" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                    d="M10.7426 10.7426C9.61742 11.8679 8.0913 12.5 6.5 12.5C4.9087 12.5 3.38258 11.8679 2.25736 10.7426C1.13214 9.61742 0.5 8.0913 0.5 6.5C0.5 4.9087 1.13214 3.38258 2.25736 2.25736C3.38258 1.13214 4.9087 0.5 6.5 0.5C8.0913 0.5 9.61742 1.13214 10.7426 2.25736C11.8679 3.38258 12.5 4.9087 12.5 6.5C12.5 8.0913 11.8679 9.61742 10.7426 10.7426ZM7.25 4.25C7.25 3.83516 6.91484 3.5 6.5 3.5C6.08516 3.5 5.75 3.83516 5.75 4.25V5.75H5L5 4.25C5 3.83516 4.66484 3.5 4.25 3.5C3.83516 3.5 3.5 3.83516 3.5 4.25L3.5 8.75C3.5 9.16484 3.83516 9.5 4.25 9.5C4.66484 9.5 5 9.16484 5 8.75V7.25L5.75 7.25V8.75C5.75 9.16485 6.08516 9.5 6.5 9.5C6.91484 9.5 7.25 9.16485 7.25 8.75V7.25H8.75C9.16484 7.25 9.5 6.91484 9.5 6.5V4.25C9.5 3.83516 9.16484 3.5 8.75 3.5C8.33516 3.5 8 3.83516 8 4.25L8 5.75H7.25V4.25Z"
                                    fill="currentColor" />
                            </svg>
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">Automaat</span>
                        </div>
                        <span class="text-base/7 text-gray-400">Transmissie</span>
                    </Button>
                    <Button class="group text-white hover:text-tanhover flex flex-col w-48" :useInertia="false" @click="popularBody">
                        <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                            <font-awesome-icon class="h-8" :icon="['fas','car-side']" />
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">Crossover</span>
                        </div>
                        <span class="text-base/7 text-gray-400">Carrosserie</span>
                    </Button>
                    <Button class="group text-white hover:text-tanhover flex flex-col w-48 md:mx-auto" :useInertia="false" @click="popularPrice">
                        <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                            <font-awesome-icon class="h-8" :icon="['fas','tag']" />
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">tot 25.000</span>
                        </div>
                        <span class="text-base/7 text-gray-400">Prijs</span>
                    </Button>
                    <Button class="group text-white hover:text-tanhover flex flex-col w-48 md:ml-auto" :useInertia="false" @click="popularFuel">
                        <div class="flex items-center gap-4 transform group-hover:-translate-y-0.5 duration-500">
                            <font-awesome-icon class="h-8" :icon="['fas','charging-station']" />
                            <span class="text-3xl font-semibold tracking-tight lg:text-4xl">Hybride</span>
                        </div>
                        <span class="text-base/7 text-gray-400">Brandstof</span>
                    </Button>
                </div>
            </div>
        </div>
        <div class="hidden lg:block absolute top-0 bottom-0 w-full h-full">
            <img :src="'/img/showroom/zwagers_automotive_show_25.webp'" alt="Zwagers Automotive" class="object-cover w-full h-full opacity-40">
        </div>
    </section>
    <Subfooter />
</template>
